import { useState } from 'react';
import { Trash2, Loader2, PauseCircle, PlayCircle, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '../ui/alert-dialog';

/** Small, discreet delete — icon only, red on hover, always confirms first. */
export function DeleteInvoiceButton({ api, item, onDeleted }) {
  const [busy, setBusy] = useState(false);
  const del = async () => {
    setBusy(true);
    try {
      await api.delete(`/cost-inbox/${item.id}`);
      toast.success('Invoice deleted from the inbox');
      onDeleted();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete'); }
    finally { setBusy(false); }
  };
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button type="button" disabled={busy} title="Delete this upload" className="p-1.5 rounded text-gray-500 hover:text-red-400 hover:bg-red-500/10 transition-colors" data-testid={`inbox-delete-btn-${item.id}`}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent className="bg-[#2A3240] border-white/10 text-white" data-testid="inbox-delete-confirm">
        <AlertDialogHeader>
          <AlertDialogTitle>Delete this invoice?</AlertDialogTitle>
          <AlertDialogDescription className="text-gray-300">
            <b className="text-white">{item.supplier_name || item.original_filename}</b>{item.reference ? ` · Ref ${item.reference}` : ''} will be permanently removed from the Cost Inbox. Use this for statements, duplicates or files uploaded by mistake.
            Nothing has been posted to the ledger, so no project costs are affected. This cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="bg-white/5 border-white/20 text-white hover:bg-white/10" data-testid="inbox-delete-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={del} className="bg-red-600 hover:bg-red-700 text-white" data-testid="inbox-delete-confirm-btn">Delete</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function HoldButton({ api, item, beforeHold, onHeld, busy }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState(item.hold_note || '');
  const [noProject, setNoProject] = useState(!!item.no_project);
  const [saving, setSaving] = useState(false);
  const hold = async () => {
    setSaving(true);
    try {
      await beforeHold?.();
      await api.post(`/cost-inbox/${item.id}/hold`, { note, no_project: noProject });
      toast.success('Invoice put on hold');
      setOpen(false);
      onHeld();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to put on hold'); }
    finally { setSaving(false); }
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" disabled={busy} className="border-purple-500/40 text-purple-200 bg-purple-500/10 hover:bg-purple-500/20" data-testid="inbox-hold-btn">
          <PauseCircle className="w-4 h-4 mr-2" /> On hold
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 bg-[#2A3240] border-white/10 text-white" data-testid="inbox-hold-popover">
        <div className="text-sm font-medium mb-1">Put on hold</div>
        <p className="text-xs text-gray-400 mb-2">Park this invoice until you have what you need (e.g. waiting on a site name or a credit note). You can re-open it later.</p>
        <label className="flex items-start gap-2 mb-2 cursor-pointer select-none rounded-md border border-white/10 bg-white/[0.03] p-2" data-testid="inbox-hold-no-project-row">
          <input type="checkbox" checked={noProject} onChange={e => setNoProject(e.target.checked)} className="mt-0.5 accent-[#F5A623] w-4 h-4" data-testid="inbox-hold-no-project" />
          <span className="text-xs text-gray-200">No project — tag this invoice so all the ones with no job assigned sit in one place. The note stays optional.</span>
        </label>
        <Textarea value={note} onChange={e => setNote(e.target.value)} className="bg-[#1E2530] border-white/20 text-white min-h-[64px] text-sm" placeholder="Why is it on hold? (optional)" data-testid="inbox-hold-note" />
        <div className="flex justify-end gap-2 mt-2">
          <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>Cancel</Button>
          <Button size="sm" onClick={hold} disabled={saving} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="inbox-hold-confirm-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <PauseCircle className="w-4 h-4 mr-1" />} Put on hold
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function ReopenButton({ api, item, onReopened, busy }) {
  const [saving, setSaving] = useState(false);
  const reopen = async () => {
    setSaving(true);
    try {
      await api.post(`/cost-inbox/${item.id}/reopen`);
      toast.success('Invoice re-opened');
      onReopened();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to re-open'); }
    finally { setSaving(false); }
  };
  return (
    <Button variant="outline" onClick={reopen} disabled={busy || saving} className="border-white/20 text-white bg-white/5 hover:bg-white/10" data-testid="inbox-reopen-btn">
      {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PlayCircle className="w-4 h-4 mr-2" />} Re-open
    </Button>
  );
}

/** Undo a delete — restore the invoice from the Deleted bin back to the inbox. */
export function RestoreButton({ api, item, onRestored }) {
  const [busy, setBusy] = useState(false);
  const restore = async () => {
    setBusy(true);
    try {
      await api.post(`/cost-inbox/${item.id}/restore`);
      toast.success('Invoice restored to the inbox');
      onRestored?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to restore'); }
    finally { setBusy(false); }
  };
  return (
    <Button size="sm" variant="outline" onClick={restore} disabled={busy} className="border-emerald-500/40 text-emerald-200 bg-emerald-500/10 hover:bg-emerald-500/20" data-testid={`inbox-restore-btn-${item.id}`}>
      {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-1" />} Restore
    </Button>
  );
}

/** Permanently remove a single binned invoice — cannot be undone. Always confirms. */
export function PurgeInvoiceButton({ api, item, onPurged }) {
  const [busy, setBusy] = useState(false);
  const purge = async () => {
    setBusy(true);
    try {
      await api.delete(`/cost-inbox/${item.id}/permanent`);
      toast.success('Invoice permanently removed');
      onPurged?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to remove'); }
    finally { setBusy(false); }
  };
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button type="button" disabled={busy} title="Delete permanently" className="p-1.5 rounded text-gray-500 hover:text-red-400 hover:bg-red-500/10 transition-colors" data-testid={`inbox-purge-btn-${item.id}`}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent className="bg-[#2A3240] border-white/10 text-white" data-testid="inbox-purge-confirm">
        <AlertDialogHeader>
          <AlertDialogTitle>Delete permanently?</AlertDialogTitle>
          <AlertDialogDescription className="text-gray-300">
            <b className="text-white">{item.supplier_name || item.original_filename}</b>{item.reference ? ` · Ref ${item.reference}` : ''} will be removed for good and <b className="text-white">cannot be restored</b>. This does not affect the cost ledger.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="bg-white/5 border-white/20 text-white hover:bg-white/10" data-testid="inbox-purge-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={purge} className="bg-red-600 hover:bg-red-700 text-white" data-testid="inbox-purge-confirm-btn">Delete permanently</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/** Empty the whole Deleted bin at once — permanent, always confirms. */
export function EmptyBinButton({ api, count, onEmptied }) {
  const [busy, setBusy] = useState(false);
  const empty = async () => {
    setBusy(true);
    try {
      const r = await api.post('/cost-inbox/bin/empty');
      toast.success(`Bin emptied — ${r.data.purged} invoice${r.data.purged === 1 ? '' : 's'} removed for good`);
      onEmptied?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to empty bin'); }
    finally { setBusy(false); }
  };
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button size="sm" variant="outline" disabled={busy || !count} className="border-red-500/40 text-red-300 bg-red-500/10 hover:bg-red-500/20 shrink-0" data-testid="inbox-empty-bin-btn">
          {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1" />} Empty bin
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent className="bg-[#2A3240] border-white/10 text-white" data-testid="inbox-empty-bin-confirm">
        <AlertDialogHeader>
          <AlertDialogTitle>Empty the bin?</AlertDialogTitle>
          <AlertDialogDescription className="text-gray-300">
            All {count} invoice{count === 1 ? '' : 's'} in the bin will be removed for good and <b className="text-white">cannot be restored</b>. This does not affect the cost ledger.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="bg-white/5 border-white/20 text-white hover:bg-white/10" data-testid="inbox-empty-bin-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={empty} className="bg-red-600 hover:bg-red-700 text-white" data-testid="inbox-empty-bin-confirm-btn">Empty bin</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
